import { logger } from '@/lib/logger';
import { supabase } from '@/lib/supabase';
import type { LessonMaterial } from '@/types';

/**
 * Signed access to protected files on files.openstudiojazz.com.
 *
 * PDF and audio materials live in R2 behind the r2-files-gateway Cloudflare
 * Worker, which only serves lessons/{id}/pdfs/* and lessons/{id}/materials/*
 * to URLs carrying a short-lived HMAC signature. The sign-file-url edge
 * function mints those signatures after checking the user is entitled to the
 * lesson (same access chain as get-lesson-new / lesson playback).
 */

const FILES_HOST = 'files.openstudiojazz.com';
const PROTECTED_PATH_RE = /^\/lessons\/\d+\/(pdfs|materials)\//;

/**
 * Material types rendered as in-page audio players (see the lesson page's
 * AudioMaterialItem branch). Players fetch the URL at render time (not on
 * play), so these are signed up front with a TTL long enough to cover a
 * practice session. Deliberately excludes 'wav': the lesson page renders wav
 * as a plain link, which opens in a browser tab — those sign per-click with
 * the short default TTL so a copied address-bar URL dies quickly.
 */
const AUDIO_TYPES = new Set(['mp3', 'm4a', 'audio', 'mp4']);
const AUDIO_TTL_SECONDS = 21600; // 6h

export function isProtectedFilesUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.hostname === FILES_HOST && PROTECTED_PATH_RE.test(parsed.pathname);
  } catch {
    return false;
  }
}

/**
 * Signs a batch of protected file URLs. Non-protected URLs are ignored.
 * Returns a map of original URL -> signed URL; on failure returns whatever
 * could be signed (possibly {}) and logs — callers fall back to the original
 * URL, which the Worker will reject, surfacing the existing error paths.
 */
export async function signFileUrls(
  urls: string[],
  ttlSeconds?: number,
): Promise<Record<string, string>> {
  const targets = [...new Set(urls.filter(isProtectedFilesUrl))];
  if (targets.length === 0) return {};

  try {
    const { data, error } = await supabase.functions.invoke('sign-file-url', {
      body: { urls: targets, ttl: ttlSeconds },
    });
    if (error) {
      logger.error('sign-file-url failed:', { error, count: targets.length });
      return {};
    }
    if (data?.denied?.length) {
      logger.warn('sign-file-url denied some URLs:', { denied: data.denied });
    }
    return data?.signed ?? {};
  } catch (err) {
    logger.error('sign-file-url threw:', { err });
    return {};
  }
}

/**
 * Returns a signed URL for a protected file, or the input unchanged when the
 * URL is not signature-gated (or signing failed).
 */
export async function getSignedFileUrl(url: string): Promise<string> {
  if (!isProtectedFilesUrl(url)) return url;
  const signed = await signFileUrls([url]);
  return signed[url] ?? url;
}

/**
 * Replaces protected audio material URLs with pre-signed ones (see
 * AUDIO_TYPES). Returns the input array unchanged (same reference) when there
 * is nothing to sign, so callers can cheaply skip a state update.
 */
export async function signAudioMaterialUrls(
  materials: LessonMaterial[],
): Promise<LessonMaterial[]> {
  const audioUrls = materials
    .filter((m) => AUDIO_TYPES.has(m.type) && isProtectedFilesUrl(m.url))
    .map((m) => m.url);
  if (audioUrls.length === 0) return materials;

  const signed = await signFileUrls(audioUrls, AUDIO_TTL_SECONDS);
  if (Object.keys(signed).length === 0) return materials;

  return materials.map((m) => (signed[m.url] ? { ...m, url: signed[m.url] } : m));
}
